import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Eye, Stethoscope, Heart, FileText, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { FunnelInsightAlert } from './FunnelInsights';
import VettingProcedureDetailDialog from './VettingProcedureDetailDialog';

const OpportunityActivityTab = ({ opportunityId, saleId }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedVetting, setSelectedVetting] = useState(null);

  const { data: activityData, isLoading: loading } = useQuery({
    queryKey: QK.oppActivity(opportunityId),
    queryFn: async () => {
      const [allViewingsRes, vettingRes, cardsRes] = await Promise.all([
        api.get(`/sale-cards/all-viewings?sale_id=${saleId}`),
        api.get(`/vetting-procedures?sale_id=${saleId}`),
        api.get(`/sale-cards?sale_id=${saleId}`),
      ]);
      const oppVettings = (vettingRes.data || []).filter(p => p.sale_opportunity_id === opportunityId);
      const cardMap = Object.fromEntries((cardsRes.data || []).map(c => [c.id, c]));
      const oppViews = (allViewingsRes.data || [])
        .filter(v => v.sale_opportunity_id === opportunityId)
        .map(v => {
          const card = cardMap[v.card_id] || {};
          return { ...v, viewer_name: v.viewer_name || card.viewer_name, card_id: v.card_id, card_type: card.card_type };
        });
      oppViews.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      return { views: oppViews, vettings: oppVettings };
    },
    enabled: !!opportunityId && !!saleId,
  });
  const views    = activityData?.views    ?? [];
  const vettings = activityData?.vettings ?? [];

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= today;
  };

  const todayViews = views.filter(v => isToday(v.updated_at || v.created_at));
  const todayVettings = vettings.filter(v => isToday(v.procedure_date_time || v.created_at));

  return (
    <>
    <div className="space-y-6">
      {/* Funnel Insight Alert */}
      <FunnelInsightAlert opportunityId={opportunityId} saleId={saleId} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Eye className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-muted-foreground">Views Today</p>
              <p className="font-serif text-lg font-semibold">{todayViews.filter(v => v.status === 'completed').length} of {todayViews.length} requested</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Eye className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-xs text-muted-foreground">Total Views</p>
              <p className="font-serif text-lg font-semibold">{views.filter(v => v.status === 'completed').length} of {views.length} requested</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Stethoscope className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-xs text-muted-foreground">Vetting Today</p>
              <p className="font-serif text-2xl font-semibold">{todayVettings.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Stethoscope className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-xs text-muted-foreground">Total Vetting</p>
              <p className="font-serif text-2xl font-semibold">{vettings.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Views Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Views ({views.length})</CardTitle></CardHeader>
        <CardContent>
          {views.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No views recorded yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Viewer</TableHead>
                  <TableHead style={{ width: '130px' }}>Created</TableHead>
                  <TableHead style={{ textAlign: 'center', width: '128px' }}>Total Card Views</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {views.map(v => (
                  <TableRow key={v.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/sales/${saleId}/cards/${v.card_id}`)}>
                    <TableCell className="text-sm font-medium">{v.viewer_name || '-'}</TableCell>
                    <TableCell className="text-sm" style={{ width: '130px' }}>{v.created_at ? new Date(v.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                    <TableCell style={{ textAlign: 'center', width: '128px' }}>
                      {v.card_viewing_count}
                      {v.card_type && v.card_type !== 'individual' && (
                        <Badge variant="outline" className="ml-1 text-xs py-0 capitalize">{v.card_type.replace(/_/g, ' ')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {v.status === 'completed' ? (
                        <span>
                          <Badge className="bg-green-100 text-green-700 text-xs py-0">Done</Badge>
                          {v.updated_at && <span className="text-xs text-muted-foreground ml-1">({new Date(v.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})</span>}
                        </span>
                      ) : v.status === 'cancelled' ? (
                        <Badge className="bg-red-100 text-red-700 text-xs py-0">Cancelled</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 text-xs py-0">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {v.liked && <span title="Liked"><Heart className="w-3.5 h-3.5 text-red-500 fill-current inline mr-1" /></span>}
                      {v.viewed_vet_report && <span title="Viewed Vet Report"><FileText className="w-3.5 h-3.5 text-blue-500 inline mr-1" /></span>}
                      {v.notes && <span className="text-muted-foreground">{v.notes}</span>}
                      {!v.liked && !v.viewed_vet_report && !v.notes && <span className="text-muted-foreground">-</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Vetting Procedures Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Vetting Procedures ({vettings.length})</CardTitle></CardHeader>
        <CardContent>
          {vettings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No vetting procedures recorded yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vet</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Date / Time</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vettings.map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedVetting(p)}>
                    <TableCell className="text-sm">
                      <span className="font-semibold">{p.vet_name || '-'}</span>
                      {p.vet_practice && <span className="block text-muted-foreground text-xs">{p.vet_practice}</span>}
                    </TableCell>
                    <TableCell className="text-sm">{p.client_name || '-'}</TableCell>
                    <TableCell className="text-sm">{p.procedure_date_time ? new Date(p.procedure_date_time).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                    <TableCell className="text-sm">
                      <span className="font-medium">{p.procedure?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-'}</span>
                      {p.viewed_vet_report && <span title="Viewed Vet Report"><FileText className="w-3.5 h-3.5 text-blue-500 inline ml-1" /></span>}
                      {p.notes && <span className="block text-muted-foreground text-xs">{p.notes}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
      <VettingProcedureDetailDialog
        procedure={selectedVetting}
        onClose={() => setSelectedVetting(null)}
        onSaved={(id) => {
          // Invalidate the activity query to get fresh vettings data
          qc.invalidateQueries({ queryKey: QK.oppActivity(opportunityId) });
          setSelectedVetting(null);
        }}
      />
    </>
  );
};

export default OpportunityActivityTab;
